/**
 * Shaping for the run-analytics charts on the Workflow optimization page.
 *
 * Everything here is derived from what the page already fetches — the job's `scores`,
 * the journal's per-slice iterations and the usage breakdown. Nothing is invented:
 * a run without `scores` renders an empty state rather than a zero, because
 * "not measured" and "measured zero" are different facts.
 */

import type {
  WorkflowTuningStepPoint,
  WorkflowEvaluationJob,
  WorkflowSliceIteration,
  WorkflowTuningJournal,
  WorkflowTuningScores,
  WorkflowTuningSummary,
} from '../types/workflowTuning';

/** The closing iteration over the global sections is not a state, so it is labelled apart. */
export const GLOBALS_SLICE = '__globals__';

export interface SliceBar {
  slice: string;
  label: string;
  isGlobals: boolean;
  /** Writable paths the slice was offered. */
  paths: number;
  /** Paths it actually rewrote. */
  changed: number;
  /** 0..1, and 0 when the slice had no paths at all — never NaN. */
  ratio: number;
  promptTokens: number;
  completionTokens: number;
  tokens: number;
  /** Path keys the model mangled beyond recovery: `discarded` + `rejected`. */
  lost: number;
  failed: boolean;
}

export function sliceLabel(slice: string): string {
  return slice === GLOBALS_SLICE ? 'global sections' : slice;
}

function count(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

export function buildSliceBars(journal: WorkflowTuningJournal | null | undefined): SliceBar[] {
  const rows: WorkflowSliceIteration[] = journal?.slice_iterations ?? [];
  return rows.map((row) => {
    const paths = count(row.paths);
    const rawChanged = count(row.changed_paths);
    const changed = paths > 0 ? Math.min(rawChanged, paths) : rawChanged;
    const promptTokens = count(row.prompt_tokens);
    const completionTokens = count(row.completion_tokens);
    return {
      slice: row.slice,
      label: sliceLabel(row.slice),
      isGlobals: row.slice === GLOBALS_SLICE,
      paths,
      changed,
      ratio: paths > 0 ? changed / paths : 0,
      promptTokens,
      completionTokens,
      // The split is optional; `tokens` is the number the contract always carries.
      tokens: count(row.tokens) || promptTokens + completionTokens,
      lost: count(row.recovery?.discarded) + count(row.recovery?.rejected),
      failed: Boolean(row.error),
    };
  });
}

/** True only when every bar carries the prompt/completion split — otherwise one segment. */
export function hasTokenSplit(bars: SliceBar[]): boolean {
  return bars.length > 0 && bars.every((bar) => bar.promptTokens + bar.completionTokens > 0);
}

export function maxTokens(bars: SliceBar[]): number {
  return bars.reduce((max, bar) => Math.max(max, bar.tokens), 0);
}

export type QualityDirection = 'up' | 'down' | 'flat';

export interface QualityView {
  initial: number;
  best: number;
  delta: number;
  direction: QualityDirection;
  /** Bar widths in percent, scaled against the larger of the two values. */
  initialPercent: number;
  bestPercent: number;
  /** Relative change, `null` when the baseline is zero and a ratio would be meaningless. */
  relative: number | null;
}

/**
 * `scores` is only present on a GET of a single finished job. Both ends are needed:
 * one number alone cannot be drawn as a before/after and must not be padded with a zero.
 */
export function qualityView(scores: WorkflowTuningScores | null | undefined): QualityView | null {
  const initial = scores?.initial;
  const best = scores?.best;
  if (typeof initial !== 'number' || typeof best !== 'number') return null;
  if (!Number.isFinite(initial) || !Number.isFinite(best)) return null;

  const delta = typeof scores?.delta === 'number' && Number.isFinite(scores.delta) ? scores.delta : best - initial;
  // Negative scores are not expected from any evaluator here, but a magnitude scale
  // keeps the drawing sane if one ever arrives.
  const scale = Math.max(Math.abs(initial), Math.abs(best));
  const percent = (value: number) => (scale > 0 ? Math.round((Math.abs(value) / scale) * 100) : 0);

  return {
    initial,
    best,
    delta,
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    initialPercent: percent(initial),
    bestPercent: percent(best),
    relative: initial !== 0 ? delta / Math.abs(initial) : null,
  };
}

/**
 * How much of the graph the run actually rewrote. `proposed_*` is what the algorithm
 * proposed — selection can still reject all of it, so this is labelled as a proposal.
 */
export interface WorkAmount {
  statesTouched: number;
  statesTotal: number;
  pathsChanged: number;
  pathsTotal: number;
  lost: number;
  failedSlices: number;
}

export function workAmount(bars: SliceBar[], summary?: WorkflowTuningSummary | null): WorkAmount {
  const states = bars.filter((bar) => !bar.isGlobals);
  return {
    statesTouched: states.filter((bar) => bar.changed > 0).length,
    // The journal only lists slices the run reached; the summary knows the wider count.
    statesTotal: states.length || count(summary?.proposed_states_changed),
    pathsChanged: bars.reduce((sum, bar) => sum + bar.changed, 0),
    pathsTotal: bars.reduce((sum, bar) => sum + bar.paths, 0),
    lost: bars.reduce((sum, bar) => sum + bar.lost, 0),
    failedSlices: bars.filter((bar) => bar.failed).length,
  };
}

export function formatScore(value: number): string {
  return value.toFixed(4);
}

export function formatSigned(value: number, digits = 4): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}`;
}

export function formatCompactTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(value);
}

// ─── Evaluation runs ────────────────────────────────────────────────────────

/**
 * Ряд по прогонам оценки интеграции — это не то же самое, что кривая внутри
 * прогона: та строится по `job.steps` (см. `stepSeries` ниже, AIT-291/AIT-292).
 * Здесь по точке на каждый отдельный прогон оценки, слева направо по времени.
 */
export interface EvalPoint {
  id: string;
  score: number;
  /** Run ordinal as shown on the axis, oldest first. */
  ordinal: number;
  at?: string;
  mode?: string;
}

export function evaluationSeries(jobs: WorkflowEvaluationJob[]): EvalPoint[] {
  return jobs
    .filter((job) => typeof job.result?.evaluation?.score === 'number' && Number.isFinite(job.result.evaluation.score))
    // The list arrives newest first; a series reads left to right in time.
    .slice()
    .sort((a, b) => String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')))
    .map((job, index) => ({
      id: job.id,
      score: job.result!.evaluation!.score as number,
      ordinal: index + 1,
      at: job.created_at,
      mode: job.evaluation_mode,
    }));
}

export interface SeriesScale {
  min: number;
  max: number;
  /** Fractional position of a score in the drawn band, 0 at the bottom. */
  place: (score: number) => number;
}

/** A flat series still needs a band, or every point would land on one edge. */
export function seriesScale(values: number[]): SeriesScale | null {
  if (values.length === 0) return null;
  const low = Math.min(...values);
  const high = Math.max(...values);
  const pad = high - low > 0 ? (high - low) * 0.18 : Math.max(Math.abs(high) * 0.05, 0.01);
  const min = low - pad;
  const max = high + pad;
  return { min, max, place: (score) => (max - min > 0 ? (score - min) / (max - min) : 0.5) };
}

// ─── Кривая по шагам прогона (AIT-291/AIT-292) ──────────────────────────────

export interface StepPoint {
  iteration: number;
  /** `null` — не измерено. Ноль сюда подставлять нельзя: график покажет провал
   *  качества там, где измерения просто не было. */
  score: number | null;
  /** Опора, от которой считается дельта, а не первый шаг оптимизации. */
  baseline: boolean;
  at?: string;
  configSha?: string;
}

export interface StepSeries {
  /** Все точки в том порядке, в каком их отдал сервер: они уже отсортированы
   *  по `iteration` и уникальны по нему, своя сортировка тут только навредит. */
  points: StepPoint[];
  /** Только измеренные — по ним строится шкала и соединяется линия. */
  measured: StepPoint[];
  baseline: StepPoint | null;
  /** Есть ли непосчитанные шаги: линия в этих местах рвётся, и это стоит подписать. */
  hasGaps: boolean;
}

/**
 * Кривая валидации по шагам одной джобы.
 *
 * Опорная точка определяется флагом `baseline`, а не номером итерации: флаг
 * ставит ML по `validation_only`, а номер — лишь запасной признак. Отсутствующий
 * или пустой `steps` — состояние «нет данных», а не ноль: у джоб, созданных до
 * раскатки, он пуст всегда.
 *
 * `scoreOf` выбирает, какое поле точки читать как score — по умолчанию
 * `validation_score`. График качества по шагам строится тем же кодом, только
 * читает `quality_score` (см. `qualityStepSeries` в WorkflowTuningCharts.tsx).
 */
export function stepSeries(
  job: { steps?: WorkflowTuningStepPoint[] } | null | undefined,
  scoreOf: (s: WorkflowTuningStepPoint) => number | null | undefined = (s) => s.validation_score
): StepSeries {
  const raw = Array.isArray(job?.steps) ? job!.steps! : [];
  const points: StepPoint[] = raw
    .filter((s) => typeof s?.iteration === 'number' && Number.isFinite(s.iteration))
    .map((s) => {
      const value = scoreOf(s);
      const score = typeof value === 'number' && Number.isFinite(value) ? value : null;
      return {
        iteration: s.iteration,
        score,
        // Номер итерации — запасной признак: полагаться на него как на основной
        // нельзя, но если флага не прислали вовсе, нулевая точка всё же опорная.
        baseline: s.baseline === true || (s.baseline === undefined && s.iteration === 0),
        at: s.recorded_at,
        configSha: s.config_sha256,
      };
    });

  const measured = points.filter((p) => p.score !== null);
  return {
    points,
    measured,
    baseline: points.find((p) => p.baseline) ?? null,
    hasGaps: points.length > measured.length,
  };
}
